import React, { useState, ReactNode } from 'react';
import { Popover, Box } from '@mui/material';
import { ColorPicker as ReactColorPicker } from 'react-color-palette';
import 'react-color-palette/css';

// Helper function to convert hex to IColor (same as used in Blocks widget)
function hexToColor(hex: string) {
  const rgb = hexToRgb(hex);
  const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);

  return {
    hex: hex.startsWith('#') ? hex : `#${hex}`,
    rgb: { ...rgb, a: 1 },
    hsv: { ...hsv, a: 1 },
  };
}

function hexToRgb(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 0, g: 0, b: 0 };
}

function rgbToHsv(r: number, g: number, b: number) {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const diff = max - min;

  let h = 0;
  const s = max === 0 ? 0 : (diff / max) * 100;
  const v = max * 100;

  if (diff !== 0) {
    if (max === r) {
      h = 60 * ((g - b) / diff);
    } else if (max === g) {
      h = 60 * (2 + (b - r) / diff);
    } else {
      h = 60 * (4 + (r - g) / diff);
    }
  }

  if (h < 0) {
    h += 360;
  }

  return { h: Math.round(h), s: Math.round(s), v: Math.round(v), a: 1 };
}

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  children: ReactNode;
  height?: number;
  disabled?: boolean;
}

export const ColorPicker: React.FC<ColorPickerProps> = ({ 
  color, 
  onChange, 
  children, 
  height = 200,
  disabled = false 
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box>
      {/* Customizable trigger element */}
      <Box onClick={handleClick} sx={{ cursor: disabled ? 'default' : 'pointer' }}>
        {children}
      </Box>
      
      {/* Color picker popup */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPopover-paper': {
            borderRadius: 2,
            overflow: 'hidden',
          }
        }}
      >
        <Box sx={{ p: 1 }}>
          <ReactColorPicker
            color={hexToColor(color)}
            onChange={(colorObj) => {
              onChange(colorObj.hex);
            }}
            height={height}
          />
        </Box>
      </Popover>
    </Box>
  );
};
